import React from 'react';
import {
  ApiFilteredAlert,
  ApiPersonType,
  ApiSanctionEntityType,
  ApiSanctionType,
  ApiScreeningMatchProvider,
} from '../../api';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import {
  BridgeApiConversationCategory,
  InternalApiAlertFilterExpressionFieldTypeEnum,
  InternalApiAlertFilterExpressionOperationEnum,
  InternalApiAlertFilterExpressionTargetEnum,
  InternalApiAlertType,
  InternalApiCaseAlert,
  InternalApiCustomList,
  InternalApiCustomSchema,
  InternalApiCustomSchemaField,
  InternalApiCustomSchemaFieldTypeEnum,
  InternalApiEntitySchema,
  InternalApiEntitySchemaField,
  InternalApiEntitySchemaFieldType,
  InternalApiFinalRiskLevel,
  InternalApiScenarioOption,
  InternalApiScenarioType,
  InternalApiScreeningAlertStatus,
  InternalApiUserBasicDetails,
} from '../../api-internal';
import { Popover, Table } from 'react-bootstrap';
import RmsPopover from '../../common/popover/RmsPopover';
import { isChildField, isComplexField, isVisible } from '../../schema/helper';
import { systemAlertStatusCodes } from '../../screening/alert-status/alertStatusesConstants';
import { getRationaleLabel, rationaleOptions } from '../manual/rationaleHelper';
import { pluralize, trimValue } from '../../common/helper';

export interface GetAlertFilterObjectsInputProps {
  users: Array<InternalApiUserBasicDetails> | undefined;
  statuses: Array<InternalApiScreeningAlertStatus>;
  personSchema?: InternalApiEntitySchema | undefined;
  transactionSchema?: InternalApiEntitySchema | undefined;
  customPersonSchema?: InternalApiCustomSchema | null;
  customLists?: InternalApiCustomList[];
  scenarios?: InternalApiScenarioOption[];
  categories?: BridgeApiConversationCategory[];
  screeningEnabled: boolean;
  monitoringEnabled: boolean;
  bridgeAlertsEnabled: boolean;
  bridgeEnabled: boolean;
  adverseMediaEntitiesEnabled: boolean;
}

export class AlertFilterObjects {
  constructor(public objects: AlertFilterObject[]) {}

  public getObjectOptions(): DropdownOption[] {
    return this.objects.map((it) => it.value);
  }

  public getObject(key: string | undefined): AlertFilterObject | undefined {
    return this.objects.find((it) => it.value.key === key);
  }

  public createNewDefaultValues(): {
    field: DropdownOption;
    operation: DropdownOption;
    fieldType: InternalApiAlertFilterExpressionFieldTypeEnum;
    target: DropdownOption;
  } {
    const object = this.objects[0];
    const field = object.fields[0];
    const operation = field.operations[0];
    return {
      target: object.value,
      field: field.value,
      operation: operation,
      fieldType: field.type,
    };
  }
}

const COMPLEX_DATA_SEPARATOR = '.';

export class AlertFilterObject {
  constructor(
    public value: DropdownOption,
    public fields: AlertFilterField[]
  ) {}

  public getFieldOptions(): DropdownOption[] {
    return this.fields
      .map((it) => it.value)
      .filter(
        (it) =>
          !it.key.includes(COMPLEX_DATA_SEPARATOR) ||
          [personExternalIdFieldKey, personTypeFieldKey, personRiskLevelFieldKey].includes(it.key)
      );
  }

  public getField(key: string): AlertFilterField | undefined {
    return this.fields.find((it) => it.value.key === key);
  }
}

export class AlertFilterField {
  constructor(
    public value: DropdownOption,
    public type: InternalApiAlertFilterExpressionFieldTypeEnum,
    public operations: DropdownOption[],
    public options?: DropdownOption[]
  ) {}

  public getOperation(key: string): DropdownOption {
    const DropdownOption = this.operations.find((it) => it.key === key);
    if (!DropdownOption) {
      throw new Error(`Field ${this.value.key} has no operation ${key}`);
    }
    return DropdownOption;
  }
}

const dateLessThanOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
  label: 'is before',
  value: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
};

const dateGreaterThanOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
  label: 'is after',
  value: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
};

const numericLessThanOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
  label: 'is less than',
  value: InternalApiAlertFilterExpressionOperationEnum.LESS_THAN,
};

const numericGreaterThanOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
  label: 'is greater than',
  value: InternalApiAlertFilterExpressionOperationEnum.GREATER_THAN,
};

const isBetweenOperation: DropdownOption = {
  key: 'IS_BETWEEN',
  label: 'is between',
  value: 'IS_BETWEEN',
};

const isOneOfOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.EQUALS_ONE_OF,
  label: 'is',
  value: InternalApiAlertFilterExpressionOperationEnum.EQUALS_ONE_OF,
};

const isNotOneOfOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.NOT_EQUALS_ONE_OF,
  label: 'is not',
  value: InternalApiAlertFilterExpressionOperationEnum.NOT_EQUALS_ONE_OF,
};

const equalsOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.EQUALS,
  label: 'is',
  value: InternalApiAlertFilterExpressionOperationEnum.EQUALS,
};

const notEqualsOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.NOT_EQUALS,
  label: 'is not',
  value: InternalApiAlertFilterExpressionOperationEnum.NOT_EQUALS,
};

const containsOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.PARTLY_MATCHES,
  label: 'contains',
  value: InternalApiAlertFilterExpressionOperationEnum.PARTLY_MATCHES,
};

const notContainsOperation: DropdownOption = {
  key: InternalApiAlertFilterExpressionOperationEnum.NO_PARTIAL_MATCH,
  label: 'does not contain',
  value: InternalApiAlertFilterExpressionOperationEnum.NO_PARTIAL_MATCH,
};

export const timestampOperations = [dateGreaterThanOperation, dateLessThanOperation, isBetweenOperation];

export const numberOperations = [
  equalsOperation,
  numericGreaterThanOperation,
  numericLessThanOperation,
  isBetweenOperation,
];

export const stringListOperations = [isOneOfOperation, isNotOneOfOperation];

export const stringOperations = [equalsOperation, containsOperation, notEqualsOperation, notContainsOperation];

const personExternalIdFieldKey = '$.personExternalID.$';
const personExternalIdField = new AlertFilterField(
  {
    key: personExternalIdFieldKey,
    label: 'Person ID',
    value: personExternalIdFieldKey,
  },
  InternalApiAlertFilterExpressionFieldTypeEnum.STRING,
  stringOperations
);

const personTypeFieldKey = '$.personType.$';
const personTypeField = new AlertFilterField(
  {
    key: personTypeFieldKey,
    label: 'Person Type',
    value: personTypeFieldKey,
  },
  InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
  stringListOperations,
  Object.keys(ApiPersonType).map((type) => ({
    key: type,
    label: type,
    value: type,
  }))
);

const personRiskLevelFieldKey = '$.personRiskLevel.$';
const personRiskLevelField = new AlertFilterField(
  {
    key: personRiskLevelFieldKey,
    label: 'Risk',
    value: personRiskLevelFieldKey,
  },
  InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
  stringListOperations,
  Object.keys(InternalApiFinalRiskLevel).map((level) => ({
    key: level,
    label: level.split('TO').join(' TO '),
    value: level,
  }))
);

const mapSchemaToField = (schemaField: InternalApiEntitySchemaField): AlertFilterField => {
  const value = {
    key: schemaField.fieldName,
    label: schemaField.displayName || schemaField.fieldName,
    value: schemaField.fieldName,
  };
  switch (schemaField.type) {
    case InternalApiEntitySchemaFieldType.KEYWORD:
    case InternalApiEntitySchemaFieldType.TEXT:
    case InternalApiEntitySchemaFieldType.ENUM:
      return new AlertFilterField(value, InternalApiAlertFilterExpressionFieldTypeEnum.STRING, stringOperations);
    case InternalApiEntitySchemaFieldType.DATETIME:
      return new AlertFilterField(value, InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP, timestampOperations);
    case InternalApiEntitySchemaFieldType.NUMBER:
      return new AlertFilterField(value, InternalApiAlertFilterExpressionFieldTypeEnum.NUMBER, numberOperations);
    default:
      throw new Error(
        `No corresponding InternalApiAlertFilterExpressionFieldTypeEnum for schema type ${schemaField.type}`
      );
  }
};

const mapCustomSchemaToField = (schemaField: InternalApiCustomSchemaField): AlertFilterField => {
  const value = {
    key: schemaField.name,
    label: schemaField.displayName || schemaField.name,
    value: schemaField.name,
  };
  switch (schemaField.type) {
    case InternalApiCustomSchemaFieldTypeEnum.STRING:
      return new AlertFilterField(value, InternalApiAlertFilterExpressionFieldTypeEnum.STRING, stringOperations);
    case InternalApiCustomSchemaFieldTypeEnum.STRINGLIST:
      return new AlertFilterField(
        value,
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        schemaField.options?.map((option) => ({
          key: option,
          label: option,
          value: option,
        }))
      );
    default:
      throw new Error(
        `No corresponding InternalApiAlertFilterExpressionFieldTypeEnum for schema type ${schemaField.type}`
      );
  }
};

export const getFilterObjects = ({
  users,
  statuses,
  personSchema,
  transactionSchema,
  customPersonSchema,
  customLists,
  scenarios,
  categories,
  screeningEnabled,
  monitoringEnabled,
  bridgeAlertsEnabled,
  bridgeEnabled,
  adverseMediaEntitiesEnabled,
}: GetAlertFilterObjectsInputProps): AlertFilterObjects => {
  const usersOptions = users?.map(
    (user) =>
      ({
        key: `${user.id}`,
        label: user.name,
        value: `${user.id}`,
      }) as DropdownOption
  );
  const dowJonesOptions: DropdownOption[] = [
    {
      key: ApiSanctionType.SANCTION,
      label: 'Sanction',
      value: ApiSanctionType.SANCTION,
    },
    {
      key: ApiSanctionType.PEP,
      label: 'PEP/RCA',
      value: ApiSanctionType.PEP,
    },
    {
      key: ApiSanctionType.ADVERSE_MEDIA,
      label: 'Adverse Media',
      value: ApiSanctionType.ADVERSE_MEDIA,
    },
  ];
  if (adverseMediaEntitiesEnabled) {
    dowJonesOptions.push({
      key: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
      label: 'Adverse Media Entity',
      value: ApiSanctionType.ADVERSE_MEDIA_ENTITY,
    });
  }
  const customListOptions =
    customLists
      ?.filter((list) => !list.code?.startsWith(ApiScreeningMatchProvider.DOW_JONES))
      ?.map(
        (list) =>
          ({
            key: list.id,
            label: list.name,
            value: list.id,
          }) as DropdownOption
      ) || [];
  const sortedScenarios = scenarios?.sort((a, b) => {
    if (a.active === b.active) {
      return a.name < b.name ? -1 : 1;
    } else {
      return a.active > b.active ? -1 : 1;
    }
  });
  const scenarioOptions = sortedScenarios?.map((scenario) => ({
    key: scenario.handle,
    label: scenario.name,
    value: scenario.handle,
  }));
  const categoriesOptions: DropdownOption[] =
    categories?.map(
      (category) =>
        ({
          key: category.handle,
          label: category.name,
          value: category.handle,
        }) as DropdownOption
    ) || [];
  const listTypeOptions = dowJonesOptions.concat(customListOptions);
  const personFields = [personExternalIdField, personTypeField]
    .concat(
      personSchema?.schemaFields
        .filter((it) => !isComplexField(it) && !isChildField(it) && isVisible(it))
        .map((it) => mapSchemaToField(it)) || []
    )
    .concat(customPersonSchema?.fields.map((it) => mapCustomSchemaToField(it)) || [])
    .concat(personRiskLevelField);
  const transactionFields = transactionSchema?.schemaFields
    .filter((it) => !isComplexField(it) && !isChildField(it) && isVisible(it))
    .map((it) => mapSchemaToField(it));

  const statusOptions = statuses
    .filter((status) => statuses.find((status2) => status2.code === status.code)?.id === status.id)
    .filter((status) => status.code != systemAlertStatusCodes.filtered)
    .map(
      (status) =>
        ({
          key: status.code,
          label: status.code,
          value: status.code,
        }) as DropdownOption
    );

  const entityTypeOptions: DropdownOption[] = [
    {
      key: ApiSanctionEntityType.PERSON,
      label: 'Person',
      value: ApiSanctionEntityType.PERSON,
    },
    {
      key: ApiSanctionEntityType.TRANSACTION,
      label: 'Transaction',
      value: ApiSanctionEntityType.TRANSACTION,
    },
  ];
  const scenarioTypeOptions: DropdownOption[] = [
    {
      key: InternalApiScenarioType.ONLINE,
      label: 'Real-time',
      value: InternalApiScenarioType.ONLINE,
    },
    {
      key: InternalApiScenarioType.OFFLINE,
      label: 'Post-event',
      value: InternalApiScenarioType.OFFLINE,
    },
    {
      key: InternalApiScenarioType.PERIODIC,
      label: 'Periodic',
      value: InternalApiScenarioType.PERIODIC,
    },
  ];

  const alertFields = [
    new AlertFilterField(
      { key: 'id', label: 'ID', value: 'id' },
      InternalApiAlertFilterExpressionFieldTypeEnum.NUMBER,
      numberOperations
    ),
  ];
  if (screeningEnabled || monitoringEnabled) {
    alertFields.push(
      new AlertFilterField(
        { key: 'entityType', label: 'Entity', value: 'listType' },
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        entityTypeOptions
      )
    );
  }
  if (screeningEnabled) {
    alertFields.push(
      new AlertFilterField(
        { key: 'listType', label: 'Matched list type', value: 'listType' },
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        listTypeOptions
      )
    );
  }
  if (monitoringEnabled) {
    alertFields.push(
      new AlertFilterField(
        {
          key: 'scenarioType',
          label: 'Scenario type',
          value: 'scenarioType',
        },
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        scenarioTypeOptions
      ),
      new AlertFilterField(
        {
          key: 'scenarioHandle',
          label: 'Scenario',
          value: 'scenarioHandle',
        },
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        scenarioOptions
      )
    );
  }
  if (bridgeAlertsEnabled || bridgeEnabled) {
    alertFields.push(
      new AlertFilterField(
        {
          key: 'categoryHandle',
          label: 'RFI category name',
          value: 'categoryHandle',
        },
        InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
        stringListOperations,
        categoriesOptions
      )
    );
  }
  alertFields.push(
    new AlertFilterField(
      {
        key: 'rationale',
        label: 'Rationale',
        value: 'rationale',
      },
      InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      rationaleOptions
    )
  );
  alertFields.push(
    new AlertFilterField(
      { key: 'assignedTo', label: 'Assigned to', value: 'assignedTo' },
      InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      usersOptions
    ),
    new AlertFilterField(
      { key: 'currentStatus', label: 'Status', value: 'currentStatus' },
      InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      statusOptions
    ),
    new AlertFilterField(
      {
        key: 'lastStatusChangeTime',
        label: 'Status set at',
        value: 'lastStatusChangeTime',
      },
      InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP,
      timestampOperations
    ),
    new AlertFilterField(
      {
        key: 'lastStatusChangeBy',
        label: 'Status set by',
        value: 'lastStatusChangeBy',
      },
      InternalApiAlertFilterExpressionFieldTypeEnum.STRING_LIST,
      stringListOperations,
      usersOptions
    ),
    new AlertFilterField(
      { key: 'createdTime', label: 'Created at', value: 'createdTime' },
      InternalApiAlertFilterExpressionFieldTypeEnum.TIMESTAMP,
      timestampOperations
    )
  );

  const alertFilterObjects: AlertFilterObject[] = [
    new AlertFilterObject(
      {
        key: InternalApiAlertFilterExpressionTargetEnum.ALERT,
        label: 'Alert',
        value: InternalApiAlertFilterExpressionTargetEnum.ALERT,
      },
      alertFields
    ),
  ];

  if (personSchema && personSchema.schemaFields.length > 0 && (screeningEnabled || monitoringEnabled)) {
    alertFilterObjects.push(
      new AlertFilterObject(
        {
          key: InternalApiAlertFilterExpressionTargetEnum.PERSON,
          label: 'Person',
          value: InternalApiAlertFilterExpressionTargetEnum.PERSON,
        },
        personFields || []
      )
    );
  }

  if (transactionSchema && transactionSchema.schemaFields.length > 0 && (screeningEnabled || monitoringEnabled)) {
    alertFilterObjects.push(
      new AlertFilterObject(
        {
          key: InternalApiAlertFilterExpressionTargetEnum.TRANSACTION,
          label: 'Transaction',
          value: InternalApiAlertFilterExpressionTargetEnum.TRANSACTION,
        },
        transactionFields || []
      )
    );
  }

  return new AlertFilterObjects(alertFilterObjects);
};

const mergeScreeningAlertDetails = (details: Array<string>): string => {
  const score = details[0] === '0' ? '' : `${details[0]}% `;
  const match = details[details.length - 1];
  const input = details.slice(1, -1).join(' ');
  return `${score}${input} ⟷ ${match}`;
};

interface AlertDetailsProps {
  alertType: InternalApiAlertType;
  reasons?: {
    reason: string;
    details: Array<string>;
  }[];
}

const formatReasonDetails = (alertType: InternalApiAlertType, details: Array<string>): string => {
  switch (alertType) {
    case InternalApiAlertType.MANUAL:
      return getRationaleLabel(details[0]);
    case InternalApiAlertType.MONITORING:
    case InternalApiAlertType.BRIDGE:
      return details[0];
    case InternalApiAlertType.SCREENING:
      return mergeScreeningAlertDetails(details);
  }
};

const getFilterResultsAlertType = (alert: ApiFilteredAlert): InternalApiAlertType => {
  if (alert.monitoringAlertId) return InternalApiAlertType.MONITORING;
  if (alert.screeningAlertId) return InternalApiAlertType.SCREENING;
  if (alert.bridgeAlertId) return InternalApiAlertType.BRIDGE;
  return InternalApiAlertType.MANUAL;
};

const AlertDetailsCell = ({ alertType, reasons }: AlertDetailsProps) => {
  if (!reasons || reasons.length === 0) return null;

  const details = formatReasonDetails(alertType, reasons[0].details);

  if (reasons.length > 1) {
    const popover = (
      <Popover id='alert-details-popover' className='alert-details-popover'>
        <Popover.Header as='h3'>Alert details</Popover.Header>
        <Popover.Body>
          <Table striped size='sm' className='card-table'>
            <thead>
              <tr>
                <th>Scenario Name</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {reasons.slice(1, 6).map((item, index) => (
                <tr key={index}>
                  <td className='text-secondary-details alert-details'>{item.reason}</td>
                  <td className='alert-details' data-testid='alert-details'>
                    {formatReasonDetails(alertType, item.details)}
                  </td>
                </tr>
              ))}
            </tbody>
            {reasons.length > 6 && (
              <tfoot className='more-hits'>
                <tr>
                  <td>
                    {reasons.slice(6).length} more {pluralize(reasons.slice(6).length, 'match', 'matches')}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </Table>
        </Popover.Body>
      </Popover>
    );
    return (
      <div className='alert-reasons'>
        <span className='alert-reason'>{trimValue(reasons[0].reason, 65, true)}</span>
        <div>
          <RmsPopover overlay={popover} placement='bottom-start'>
            <div>
              {details} •{' '}
              <span className='hover-link' data-testid='hover-link'>
                {reasons.length - 1} more
              </span>
            </div>
          </RmsPopover>
        </div>
      </div>
    );
  }

  return (
    <div className='alert-reasons'>
      <span className='alert-reason'>{trimValue(reasons[0].reason, 65, true)}</span>
      <div>
        <span>{details}</span>
      </div>
    </div>
  );
};

export const CaseAlertDetailsCell = ({ caseAlert }: { caseAlert: InternalApiCaseAlert }) => (
  <AlertDetailsCell alertType={caseAlert.alert.type} reasons={caseAlert.alert.reasons} />
);

export const FilterResultsAlertDetailsCell = ({ alert }: { alert: ApiFilteredAlert }) => (
  <AlertDetailsCell alertType={getFilterResultsAlertType(alert)} reasons={alert.reasons} />
);
